/**
 * SeatPanel — full-featured seat editor for use inside the SlidePanel.
 * Replaces SeatDetailPage navigation: every field is editable inline here.
 *
 * Sections:
 *  - Title + slug (inline edit)
 *  - Reports to (parent seat select)
 *  - Allow multiple holders (switch)
 *  - Teams (read-only badges; team membership of a seat is set elsewhere)
 *  - Holders (assign / remove / set primary, with optional territory)
 *  - Role description (markdown inline edit)
 *  - Decision rights (add / remove with type)
 */
import { useState } from 'react';
import {
  X, Pencil, Check, Loader2, Star, Trash2, UserPlus, Plus,
  User as UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import { MarkdownRenderer } from './MarkdownRenderer';
import {
  useSeats,
  useUpdateSeat,
  useAssignHolder,
  useRemoveHolder,
  useSetPrimaryHolder,
} from '../api/seats';
import { useEmployees } from '@/features/teams/api/employees';
import type { Seat } from '../types';

const TEAM_COLORS: Record<string, string> = {
  Leadership: 'bg-purple-100 text-purple-900',
  Sales: 'bg-blue-100 text-blue-900',
  Operations: 'bg-orange-100 text-orange-900',
  RnD: 'bg-green-100 text-green-900',
  Finances: 'bg-yellow-100 text-yellow-900',
  System: 'bg-gray-100 text-gray-900',
};

const DR_TYPE_COLORS = {
  own: 'bg-green-100 text-green-800',
  recommend: 'bg-blue-100 text-blue-800',
  input: 'bg-purple-100 text-purple-800',
  informed: 'bg-zinc-100 text-zinc-700',
} as const;

type DrType = keyof typeof DR_TYPE_COLORS;

function holderName(h: Seat['holders'][number]): string {
  return [h.firstname, h.lastname].filter(Boolean).join(' ') || h.email || 'Unknown';
}

// Inline text/textarea editor (click-to-edit, save on check, cancel on X)
function InlineText({
  value, onSave, multiline = false, placeholder = 'Click to edit', renderMarkdown = false,
}: {
  value: string | null;
  onSave: (v: string | null) => Promise<void>;
  multiline?: boolean;
  placeholder?: string;
  renderMarkdown?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    setSaving(true);
    try { await onSave(draft.trim() || null); setEditing(false); } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        {multiline ? (
          <Textarea rows={6} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        ) : (
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={commit} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDraft(value ?? ''); setEditing(false); }}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1 group relative min-h-[2rem]"
    >
      {value ? (
        renderMarkdown
          ? <div className="prose prose-sm max-w-none"><MarkdownRenderer>{value}</MarkdownRenderer></div>
          : <span>{value}</span>
      ) : (
        <span className="text-muted-foreground italic">{placeholder}</span>
      )}
      <Pencil className="absolute top-1 right-1 h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
    </div>
  );
}

// =============================================================================

interface Props {
  seat: Seat;
  onClose: () => void;
}

export function SeatPanel({ seat, onClose }: Props) {
  const update = useUpdateSeat();
  const assign = useAssignHolder();
  const remove = useRemoveHolder();
  const setPrimary = useSetPrimaryHolder();
  const { data: seatsData } = useSeats();
  const allSeats = seatsData?.seats ?? [];
  const { data: employeesData } = useEmployees();
  const employees = employeesData?.employees ?? [];

  const saveField = (field: keyof Seat) => async (value: string | null) => {
    await update.mutateAsync({ id: seat.id, input: { [field]: value } });
  };

  // Holder add/remove state
  const [adding, setAdding] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [territory, setTerritory] = useState('');
  const holderUserIds = new Set(seat.holders.map((h) => h.user_id));
  const availableEmployees = employees.filter((e) => !holderUserIds.has(e.id));

  const addHolder = async () => {
    if (!selectedUser) return;
    await assign.mutateAsync({
      seatId: seat.id,
      input: {
        user_id: selectedUser,
        territory: territory || null,
        is_primary: seat.holders.length === 0,
      },
    });
    setAdding(false); setSelectedUser(''); setTerritory('');
  };

  // Decision rights state
  const [drAdding, setDrAdding] = useState(false);
  const [drDraft, setDrDraft] = useState('');
  const [drType, setDrType] = useState<DrType>('own');
  const saveRights = (rights: typeof seat.decision_rights) =>
    update.mutateAsync({ id: seat.id, input: { decision_rights: rights } });
  const addRight = async () => {
    if (!drDraft.trim()) return;
    await saveRights([...seat.decision_rights, { decision: drDraft.trim(), type: drType }]);
    setDrDraft(''); setDrAdding(false);
  };
  const removeRight = (idx: number) => saveRights(seat.decision_rights.filter((_, i) => i !== idx));

  const parentOptions = allSeats.filter((s) => s.id !== seat.id);
  const setParent = async (value: string) => {
    await update.mutateAsync({
      id: seat.id,
      input: { parent_seat_id: value === '__none__' ? null : value },
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold truncate">{seat.title}</h2>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {seat.teams.map((t) => (
              <Badge key={t.id} variant="secondary" className={cn('text-[10px]', TEAM_COLORS[t.name] ?? '')}>
                {t.name}
              </Badge>
            ))}
            {seat.allow_multiple && (
              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-800">Multi-holder</Badge>
            )}
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Identity */}
        <section className="space-y-3">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Seat</div>
          <div>
            <Label className="text-xs">Title</Label>
            <InlineText value={seat.title} onSave={saveField('title')} />
          </div>
          <div>
            <Label className="text-xs">Slug</Label>
            <InlineText value={seat.slug} onSave={saveField('slug')} placeholder="kebab-case-id" />
          </div>
          <div>
            <Label className="text-xs">Reports to</Label>
            <Select value={seat.parent_seat_id ?? '__none__'} onValueChange={setParent}>
              <SelectTrigger className="h-8 mt-1">
                <SelectValue>
                  {parentOptions.find((s) => s.id === seat.parent_seat_id)?.title ?? 'None (root seat)'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><em>None (root seat)</em></SelectItem>
                {parentOptions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between pt-1">
            <Label className="text-xs">Allow multiple holders</Label>
            <Switch
              checked={seat.allow_multiple}
              onCheckedChange={(checked) =>
                update.mutate({ id: seat.id, input: { allow_multiple: checked } })
              }
            />
          </div>
        </section>

        {/* Holders */}
        <section className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Holders ({seat.holders.length})
            </div>
            {(seat.allow_multiple || seat.holders.length === 0) && !adding && (
              <Button size="sm" variant="outline" className="h-7" onClick={() => setAdding(true)}>
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            )}
          </div>

          {adding && (
            <div className="border rounded-md p-2 space-y-2 bg-muted/30">
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select employee…" />
                </SelectTrigger>
                <SelectContent>
                  {availableEmployees.length === 0 ? (
                    <div className="p-2 text-xs text-muted-foreground">All employees already assigned.</div>
                  ) : availableEmployees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {[e.firstname, e.lastname].filter(Boolean).join(' ') || e.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {seat.allow_multiple && (
                <Input
                  className="h-8 text-sm"
                  placeholder="Territory (optional)"
                  value={territory}
                  onChange={(e) => setTerritory(e.target.value)}
                />
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={addHolder} disabled={!selectedUser || assign.isPending}>
                  {assign.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Assign'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setSelectedUser(''); setTerritory(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {seat.holders.length === 0 ? (
            <Badge variant="outline" className="bg-zinc-50 text-zinc-500">
              <UserIcon className="h-3 w-3 mr-1" /> Vacant
            </Badge>
          ) : (
            <div className="space-y-1.5">
              {seat.holders.map((h) => (
                <div
                  key={h.id}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-2 py-1.5',
                    h.is_primary ? 'bg-amber-50 border-amber-200' : 'bg-muted/30',
                  )}
                >
                  <OwnerAvatar
                    ownerId={h.user_id}
                    photoUrl={h.profile_photo_url}
                    name={holderName(h)}
                    className="h-7 w-7"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1">
                      {holderName(h)}
                      {h.is_primary && <Star className="h-3 w-3 fill-amber-500 text-amber-500" />}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {h.email}{h.territory && ` · ${h.territory}`}
                    </div>
                  </div>
                  {!h.is_primary && (
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => setPrimary.mutate({ seatId: seat.id, userId: h.user_id })}
                      title="Set as primary seat for this user"
                    >
                      <Star className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => {
                      if (confirm(`Remove ${holderName(h)} from ${seat.title}?`)) {
                        remove.mutate({ seatId: seat.id, userId: h.user_id });
                      }
                    }}
                    title="Remove holder"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Description */}
        <section className="space-y-2 border-t pt-4">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Role description</div>
          <InlineText
            value={seat.description}
            onSave={saveField('description')}
            multiline
            renderMarkdown
            placeholder="Describe the mission and responsibilities…"
          />
        </section>

        {/* Decision rights */}
        <section className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Decision rights</div>
            {!drAdding && (
              <Button size="sm" variant="outline" className="h-7" onClick={() => setDrAdding(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            )}
          </div>

          {drAdding && (
            <div className="flex flex-wrap gap-1.5 items-center border rounded-md p-2 bg-muted/30">
              <Input
                className="h-8 text-sm flex-1 min-w-[140px]"
                placeholder="Decision…"
                value={drDraft}
                onChange={(e) => setDrDraft(e.target.value)}
                autoFocus
              />
              <Select value={drType} onValueChange={(v) => setDrType(v as DrType)}>
                <SelectTrigger className="h-8 text-sm w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="own">Own</SelectItem>
                  <SelectItem value="recommend">Recommend</SelectItem>
                  <SelectItem value="input">Input</SelectItem>
                  <SelectItem value="informed">Informed</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={addRight}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => { setDrAdding(false); setDrDraft(''); }}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {seat.decision_rights.length === 0 && !drAdding ? (
            <div className="text-xs text-muted-foreground italic py-2">No decision rights defined.</div>
          ) : (
            <div className="space-y-1">
              {seat.decision_rights.map((dr, idx) => (
                <div key={idx} className="flex items-center gap-2 group hover:bg-muted/40 rounded px-1 py-0.5">
                  <Badge variant="secondary" className={cn(DR_TYPE_COLORS[dr.type as DrType], 'text-[10px] w-20 justify-center')}>
                    {dr.type}
                  </Badge>
                  <span className="text-sm flex-1">{dr.decision}</span>
                  <Button
                    size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRight(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-background border-t px-4 py-2 flex items-center justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

export default SeatPanel;
